import { Icon } from "@iconify/react";
import React from "react";
import { Link } from "react-router-dom";
import "../../internal/YouWoConsumerLanding.css";
import ModernFooter from "../../../components/landing/modern/ModernFooter";
import SEOHead from "../../../components/tools/SEOHead";
import ToolsLandingNav from "../../../components/tools/ToolsLandingNav";
import "../../../components/tools/ToolsLandingShell.css";
import { buildAppLink } from "../../../utils/landingRedirect";
import "./PDFToStudyGuide.css";

const features = [
  {
    icon: "lucide:file-text",
    title: "Source map",
    description: "Break the PDF into page-backed sections, key terms, and likely exam priorities.",
  },
  {
    icon: "lucide:clipboard-list",
    title: "Practice questions",
    description: "Turn the same reading into quiz questions with answers you can verify in the source.",
  },
  {
    icon: "lucide:layers",
    title: "Flashcards",
    description: "Create focused cards for definitions, formulas, dates, and distinctions.",
  },
  {
    icon: "lucide:message-circle",
    title: "Follow-up chat",
    description: "Ask for simpler explanations, lecture comparisons, or a one-page review sheet.",
  },
];

const sourceMap = [
  ["p. 4", "Encoding", "How information enters memory and why attention changes what gets stored."],
  ["p. 7", "Working memory", "Temporary active processing with limited capacity."],
  ["p. 12", "Long-term memory", "Durable storage, consolidation, and retrieval paths."],
  ["p. 18", "Retrieval cues", "Signals that help bring stored information back into awareness."],
] as const;

const prompts = [
  "Turn this PDF into a study guide with key concepts, definitions, page references, and practice questions.",
  "Extract the top 20 exam-worthy facts from this PDF and explain why each one matters.",
  "Create flashcards from this PDF. Keep each card focused on one idea and cite the page.",
  "Make a quiz from this PDF. Include answers and source references.",
  "Compare this PDF with my lecture recording and tell me what overlaps.",
];

const faqs = [
  {
    question: "What is a PDF to study guide tool?",
    answer:
      "A PDF to study guide tool turns a document into a structured learning aid with summaries, key concepts, definitions, quiz questions, flashcards, and source references.",
  },
  {
    question: "How is a study guide different from a PDF summary?",
    answer:
      "A PDF summary gives you the gist. A study guide helps you understand what to remember, what to practice, and where to verify each point in the source.",
  },
  {
    question: "Can I make flashcards and quizzes from the same PDF?",
    answer:
      "Yes. YouWo can use the same uploaded PDF to create a study guide, generate flashcards, build practice questions, and answer follow-up questions about the source.",
  },
  {
    question: "Why do page references matter?",
    answer:
      "Page references help you check the AI output against the original PDF, which matters for classes, citations, technical readings, and exam review.",
  },
];

const relatedLinks = [
  { name: "PDF Summarizer", href: "/tools/pdf-summarizer", icon: "lucide:file-text" },
  { name: "PDF to Flashcards", href: "/tools/pdf-to-flashcards", icon: "lucide:layers" },
  { name: "AI Flashcard Generator", href: "/tools/ai-flashcard-generator", icon: "lucide:copy-check" },
  { name: "AI Quiz Generator", href: "/tools/quiz-generator", icon: "lucide:help-circle" },
  { name: "Lecture Notes AI", href: "/tools/lecture-notes", icon: "lucide:graduation-cap" },
  { name: "ChatGPT for Students", href: "/tools/chatgpt-for-students", icon: "lucide:message-circle" },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebApplication",
      "name": "PDF to Study Guide - YouWo",
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "Web",
      "url": "https://youwo.ai/tools/pdf-to-study-guide",
      "description":
        "Turn any PDF into a source-cited study guide with key concepts, summaries, quiz questions, flashcards, and page references.",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
      },
    },
    {
      "@type": "FAQPage",
      "mainEntity": faqs.map((faq) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer,
        },
      })),
    },
  ],
};

const PDFToStudyGuide: React.FC = () => {
  return (
    <>
      <SEOHead
        title="PDF to Study Guide - Turn PDFs Into Notes, Quizzes & Flashcards | YouWo"
        description="Turn any PDF into a source-cited study guide with key concepts, summaries, quiz questions, flashcards, and page references. Learn from your PDFs faster with YouWo."
        keywords="pdf to study guide, turn pdf into study guide, ai study guide generator, pdf study guide generator, study guide from pdf, pdf to quiz and flashcards"
        canonicalUrl="https://youwo.ai/tools/pdf-to-study-guide"
        structuredData={structuredData}
      />

      <main className="yw-consumer-page yw-tools-page yw-study-page" data-lang="en" data-variant="source">
        <ToolsLandingNav links={[
          { label: "Demo", href: "#product" },
          { label: "Guide", href: "#guide" },
          { label: "FAQ", href: "#faq" },
        ]} />

        <section className="yw-consumer-hero yw-study-hero">
          <div className="yw-consumer-hero-copy yw-study-hero-copy">
            <p className="yw-study-kicker">PDF to Study Guide</p>
            <h1>
              Turn any PDF into a <span>source-backed</span> study system
            </h1>
            <p>
              Upload a chapter, handout, or research paper. YouWo turns it into cited notes, quiz questions,
              flashcards, and follow-up prompts that stay tied to the original pages.
            </p>
          </div>

          <div className="yw-capture-box yw-study-capture" aria-label="PDF study guide prompt preview">
            <textarea
              aria-label="Prompt preview"
              readOnly
              value="Make a cited study guide from Psychology Chapter 5.pdf with quizzes and flashcards."
            />
            <div className="yw-capture-footer">
              <a className="yw-capture-attach" href={buildAppLink()} target="_blank" rel="noopener noreferrer">
                <Icon icon="lucide:paperclip" />
                Add PDF
              </a>
              <a className="yw-capture-submit" href={buildAppLink()} target="_blank" rel="noopener noreferrer">
                Generate guide
                <Icon icon="lucide:arrow-up-right" />
              </a>
            </div>
          </div>

          <div className="yw-example-row yw-study-proof-row" aria-label="Study guide outputs">
            {["Page citations", "Quiz questions", "Flashcards", "Lecture comparison"].map((item) => (
              <span key={item}>
                <Icon icon="lucide:check" />
                {item}
              </span>
            ))}
          </div>
        </section>

        <StudyGuideDemo />
        <StudyGuideComparison />
        <StudyGuideFeatures />
        <StudyGuideArticle />
        <PromptSection />
        <FAQSection />
        <RelatedTools />
        <FinalCTA />
      </main>

      <div className="yw-study-footer-shell">
        <ModernFooter />
      </div>
    </>
  );
};

const StudyGuideDemo: React.FC = () => (
  <section className="yw-source-demo yw-study-demo" id="product" aria-label="PDF to study guide demo">
    <div className="yw-source-demo-header">
      <p>Source workflow</p>
      <h2>Start with the PDF. Finish with study actions.</h2>
      <span>
        The page references stay visible while YouWo creates the guide, so a student can jump back to the source
        instead of trusting a detached summary.
      </span>
    </div>

    <div className="yw-source-workbench yw-study-workbench">
      <section className="yw-source-recording yw-study-pdf-card" aria-label="Uploaded PDF">
        <div className="yw-source-card-heading">
          <Icon icon="lucide:file-text" />
          <div>
            <h3>Psychology Chapter 5.pdf</h3>
            <span>40 pages, uploaded to YouWo</span>
          </div>
        </div>
        <div className="yw-study-doc-preview" aria-hidden="true">
          <span />
          <span />
          <span />
          <strong>Memory Systems</strong>
          <span />
          <span />
        </div>
      </section>

      <section className="yw-source-stack yw-study-source-map" aria-label="Source map">
        <h3>Source map</h3>
        {sourceMap.map(([page, title, detail]) => (
          <div className="yw-source-mini-card" key={page}>
            <Icon icon="lucide:bookmark" />
            <div>
              <strong>{page} {title}</strong>
              <span>{detail}</span>
            </div>
          </div>
        ))}
      </section>

      <section className="yw-source-stack yw-source-stack-connected yw-study-output-stack" aria-label="Generated outputs">
        <h3>Generated outputs</h3>
        {[
          ["lucide:book-open-check", "Study guide", "Overview, concepts, and review order"],
          ["lucide:help-circle", "Quiz", "Questions with answer sources"],
          ["lucide:copy-check", "Flashcards", "One idea per card"],
        ].map(([icon, label, detail]) => (
          <div className="yw-source-mini-card" key={label}>
            <Icon icon={icon} />
            <div>
              <strong>{label}</strong>
              <span>{detail}</span>
            </div>
          </div>
        ))}
      </section>

      <section className="yw-source-answer yw-study-answer" aria-label="Source backed study guide answer">
        <p>Generated study guide</p>
        <div className="yw-source-question">
          <Icon icon="lucide:message-circle" />
          <span>What should I study first from this PDF?</span>
        </div>
        <div className="yw-source-answer-card">
          <Icon icon="lucide:sparkles" />
          <span>
            Start with working memory limits, then practice retrieval cues. These two sections connect most of the
            chapter and appear in the review questions.
          </span>
        </div>
        <div className="yw-source-citations">
          {[
            ["lucide:file-text", "Working memory", "p. 7"],
            ["lucide:file-text", "Retrieval cues", "p. 18"],
            ["lucide:help-circle", "Practice set", "6 questions"],
          ].map(([icon, label, detail]) => (
            <button key={label} type="button">
              <Icon icon={icon} />
              <strong>{label}</strong>
              <span>{detail}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  </section>
);

const StudyGuideComparison: React.FC = () => (
  <section className="yw-flow-section yw-study-compare-section">
    <p>Before and after</p>
    <h2>Not a shorter PDF. A path through it.</h2>
    <div className="yw-study-compare-grid">
      <article className="yw-study-compare-card yw-study-before-card">
        <span>
          <Icon icon="lucide:x-circle" />
          Before
        </span>
        <h3>This chapter covers memory.</h3>
        <p>
          It discusses short-term memory, long-term memory, encoding, retrieval, and forgetting. These concepts are
          important for psychology.
        </p>
        <small>No page references. No practice plan. No signal for what to review first.</small>
      </article>
      <article className="yw-study-compare-card yw-study-after-card">
        <span>
          <Icon icon="lucide:check-circle-2" />
          After
        </span>
        <h3>Working memory is the first review anchor.</h3>
        <p>
          Study the capacity limit on page 7, then connect it to retrieval cues on page 18 with the practice question
          set below.
        </p>
        <small>Includes citations, study priority, quiz prompt, and flashcard seed.</small>
      </article>
    </div>
  </section>
);

const StudyGuideFeatures: React.FC = () => (
  <section className="yw-flow-section yw-study-feature-section">
    <p>What YouWo creates</p>
    <h2>One PDF becomes a reusable study system.</h2>
    <div className="yw-flow-grid">
      {features.map((feature) => (
        <article key={feature.title}>
          <Icon icon={feature.icon} />
          <h3>{feature.title}</h3>
          <p>{feature.description}</p>
        </article>
      ))}
    </div>
  </section>
);

const StudyGuideArticle: React.FC = () => (
  <section className="yw-study-article-section" id="guide">
    <article className="yw-study-article">
      <p>Guide</p>
      <h2>PDF to study guide: how to turn any PDF into notes, quizzes, and flashcards</h2>
      <section>
        <h3>What is a PDF study guide?</h3>
        <p>
          A PDF study guide is a structured version of a document that helps you learn from it. Instead of rereading
          the full PDF again and again, you get a clear path through the material.
        </p>
        <p>
          A useful guide usually includes a short overview, important sections by page, key concepts, likely exam
          topics, practice questions, flashcards, and follow-up prompts for deeper understanding.
        </p>
      </section>

      <section>
        <h3>Why PDF summaries are not enough</h3>
        <p>
          A summary answers what the document is about. A study guide answers what you should understand, remember,
          and practice. That matters because most learning breaks down after the first read.
        </p>
      </section>

      <section>
        <h3>What makes YouWo useful for PDF studying?</h3>
        <p>
          YouWo is designed around source-backed learning. Upload PDFs into a reusable workspace, ask questions across
          documents, generate summaries, quizzes, flashcards, and notes, then return to the original source when you
          need to verify an answer.
        </p>
      </section>
    </article>
  </section>
);

const PromptSection: React.FC = () => (
  <section className="yw-flow-section yw-study-prompt-section">
    <p>Prompt ideas</p>
    <h2>Best prompts for PDF study guides</h2>
    <div className="yw-study-prompt-list">
      {prompts.map((prompt) => (
        <div key={prompt}>
          <Icon icon="lucide:message-square-text" />
          <span>{prompt}</span>
        </div>
      ))}
    </div>
  </section>
);

const FAQSection: React.FC = () => (
  <section className="yw-study-faq-section" id="faq">
    <div className="yw-section-heading">
      <p>FAQ</p>
      <h2>PDF to study guide FAQ</h2>
    </div>
    <div className="yw-study-faq-list">
      {faqs.map((faq) => (
        <article key={faq.question}>
          <h3>{faq.question}</h3>
          <p>{faq.answer}</p>
        </article>
      ))}
    </div>
  </section>
);

const RelatedTools: React.FC = () => (
  <section className="yw-study-related-section" aria-label="Related study tools">
    <div className="yw-section-heading">
      <p>Related tools</p>
      <h2>Keep building from the same source.</h2>
    </div>
    <div className="yw-study-related-grid">
      {relatedLinks.map((link) => (
        <Link key={link.href} to={link.href}>
          <Icon icon={link.icon} />
          <span>{link.name}</span>
        </Link>
      ))}
    </div>
  </section>
);

const FinalCTA: React.FC = () => (
  <section className="yw-study-final-cta">
    <div>
      <p>Ready for the next reading?</p>
      <h2>Turn your next PDF into something you can actually study from.</h2>
      <a href={buildAppLink()} target="_blank" rel="noopener noreferrer">
        Open YouWo
        <Icon icon="lucide:arrow-up-right" />
      </a>
    </div>
  </section>
);

export default PDFToStudyGuide;
